/** @jsxImportSource @emotion/react */
import React from "react";
import { SerializedStyles, css } from "@emotion/react";
import { FieldSkeleton } from "./FieldSkeleton";
import "react-datepicker/dist/react-datepicker.css";
import { returnInputStyle } from "./TextField";
import DatePicker from "react-datepicker";
import { StyledText } from "../../../assets/typography/typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { colors } from "../../colors";
import moment, { Moment } from "moment";

type Props = {
  value?: string;
  onChange: (date: string) => void;
  errorMessage?: string;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  showTimeSelect?: boolean;
  placeholder?: string;
  minDate?: Date;
  additionalStyling?: SerializedStyles;
  maxDate?: Date;
  className?: string;
  labelIcon?: React.ReactNode;
  width?: string;
  showMonthYearPicker?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
};

const floatingElementStyle = css`
  position: absolute;
  top: 50%;
  display: flex;
  right: 8px;
  text-align: center;
  transform: translate(-50%, -50%);
`;

const wrapperStyle = css`
  position: relative;
  width: 100%;
  > .react-datepicker-wrapper {
    width: 100%;
  }
`;

export const DatePickerField = ({
  label,
  errorMessage,
  helperText,
  labelIcon,
  disabled,
  showTimeSelect,
  additionalStyling,
  value,
  placeholder,
  minDate,
  onChange,
  maxDate,
  className,
  width,
  showMonthYearPicker,
  innerRef,
}: Props) => {
  let format: string = "d MMM, yyyy";

  if (showMonthYearPicker) {
    format = "MMMM, yyyy";
  }

  if (showTimeSelect) {
    format = `${format}, HH:mm`;
  }

  return (
    <FieldSkeleton
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      labelIcon={labelIcon}
      width={width}
    >
      <div css={wrapperStyle}>
        <DatePicker
          className={className}
          css={[returnInputStyle(errorMessage), additionalStyling]}
          placeholderText={placeholder || "17 Apr, 2025"}
          disabled={disabled}
          showMonthYearPicker={showMonthYearPicker}
          dateFormat={format}
          showTimeSelect={showTimeSelect}
          minDate={minDate}
          selected={value ? new Date(value) : null}
          onChange={(date) => {
            let momentDate: Moment | null = null;
            if (showTimeSelect) {
              momentDate = date ? moment(date) : null;
            } else {
              momentDate = date
                ? moment(date).hour(23).minute(59).second(59)
                : null;
            }

            onChange(momentDate ? momentDate.format() : "");
          }}
          maxDate={maxDate}
          containerRef={innerRef}
          popperProps={{
            strategy: "fixed",
          }}
        />
        <StyledText
          css={floatingElementStyle}
          className="bodyTextSemibold mb-0"
          color={colors.textPrimary}
        >
          <FontAwesomeIcon icon={solid("calendar-day")} />
        </StyledText>
      </div>
    </FieldSkeleton>
  );
};
